import { ChangeDetectionStrategy, Component, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AddFormComponent,
  FormField,
} from '@shared/components/add-form/add-form.component';

@Component({
  selector: 'app-transfer-add',
  standalone: true,
  imports: [AddFormComponent, TranslateModule],
  templateUrl: './transfer-add.component.html',
  styleUrls: ['./transfer-add.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransferAddComponent {
  @ViewChild(AddFormComponent) addFormComponent?: AddFormComponent;

  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private skipCanDeactivate = false;

  title = 'virtual_warehouse_transfer.form.add.title';

  formFields: FormField[] = [
    // Basic Information Section
    {
      label: 'virtual_warehouse_transfer.form.fields.transfer_order_number',
      controlName: 'transferOrderNumber',
      type: 'text',
      placeholder: 'virtual_warehouse_transfer.form.placeholders.auto_generated',
      required: true,
      disabled: true,
      section: 'virtual_warehouse_transfer.form.sections.basic_information',
      defaultValue:
        'TR-' +
        new Date().getFullYear() +
        '-' +
        String(Math.floor(Math.random() * 1000)).padStart(3, '0'),
    },
    {
      label: 'virtual_warehouse_transfer.form.fields.source_virtual_warehouse',
      controlName: 'sourceWarehouse',
      type: 'dropdown',
      placeholder: 'virtual_warehouse_transfer.form.placeholders.select_source_warehouse',
      required: true,
      section: 'virtual_warehouse_transfer.form.sections.basic_information',
      options: [
        { label: 'virtual_warehouse_transfer.virtual_warehouse_types.default', value: 'default-virtual' },
        { label: 'virtual_warehouse_transfer.virtual_warehouse_types.quantity', value: 'qty-virtual' },
        { label: 'virtual_warehouse_transfer.virtual_warehouse_types.interline', value: 'interline-virtual' },
      ],
    },
    {
      label: 'virtual_warehouse_transfer.form.fields.destination_virtual_warehouse',
      controlName: 'destinationWarehouse',
      type: 'dropdown',
      placeholder: 'virtual_warehouse_transfer.form.placeholders.select_destination_warehouse',
      required: true,
      section: 'virtual_warehouse_transfer.form.sections.basic_information',
      options: [
        { label: 'virtual_warehouse_transfer.virtual_warehouse_types.default', value: 'default-virtual' },
        { label: 'virtual_warehouse_transfer.virtual_warehouse_types.quantity', value: 'qty-virtual' },
        { label: 'virtual_warehouse_transfer.virtual_warehouse_types.interline', value: 'interline-virtual' },
      ],
    },
    {
      label: 'virtual_warehouse_transfer.form.fields.transfer_quantity',
      controlName: 'transferQuantity',
      type: 'number',
      placeholder: 'virtual_warehouse_transfer.form.placeholders.enter_transfer_quantity',
      required: true,
      section: 'virtual_warehouse_transfer.form.sections.basic_information',
      min: 1,
    },
    {
      label: 'virtual_warehouse_transfer.form.fields.remarks',
      controlName: 'remark',
      type: 'text',
      placeholder: 'virtual_warehouse_transfer.form.placeholders.enter_remarks',
      required: false,
      maxLength: 255,
      section: 'virtual_warehouse_transfer.form.sections.basic_information',
      colspan: 1,
    },
    // Product Details Section
    {
      label: 'virtual_warehouse_transfer.form.fields.product_code',
      controlName: 'productCode',
      type: 'text',
      placeholder: 'virtual_warehouse_transfer.form.placeholders.enter_product_code',
      required: true,
      maxLength: 255,
      section: 'virtual_warehouse_transfer.form.sections.product_details',
    },
    {
      label: 'virtual_warehouse_transfer.form.fields.product_name',
      controlName: 'productName',
      type: 'text',
      placeholder: 'virtual_warehouse_transfer.form.placeholders.enter_product_name',
      required: true,
      maxLength: 255,
      section: 'virtual_warehouse_transfer.form.sections.product_details',
    },
    {
      label: 'virtual_warehouse_transfer.form.fields.sku_code',
      controlName: 'skuCode',
      type: 'text',
      placeholder: 'virtual_warehouse_transfer.form.placeholders.enter_sku_code',
      required: true,
      maxLength: 255,
      section: 'virtual_warehouse_transfer.form.sections.product_details',
    },
    {
      label: 'virtual_warehouse_transfer.form.fields.sku_name',
      controlName: 'skuName',
      type: 'text',
      placeholder: 'virtual_warehouse_transfer.form.placeholders.enter_sku_name',
      required: true,
      maxLength: 255,
      section: 'virtual_warehouse_transfer.form.sections.product_details',
    },
    {
      label: 'virtual_warehouse_transfer.form.fields.source_virtual_warehouse_stock',
      controlName: 'sourceStock',
      type: 'number',
      placeholder: 'virtual_warehouse_transfer.form.placeholders.available_stock_in_source',
      required: false,
      section: 'virtual_warehouse_transfer.form.sections.product_details',
      disabled: true,
    },
    {
      label: 'virtual_warehouse_transfer.form.fields.available_transfer_quantity',
      controlName: 'availableQuantity',
      type: 'number',
      placeholder: 'virtual_warehouse_transfer.form.placeholders.maximum_quantity_can_transfer',
      required: false,
      section: 'virtual_warehouse_transfer.form.sections.product_details',
      disabled: true,
    },
    {
      label: 'virtual_warehouse_transfer.form.fields.destination_virtual_warehouse_stock',
      controlName: 'destinationStock',
      type: 'number',
      placeholder: 'virtual_warehouse_transfer.form.placeholders.current_stock_in_destination',
      required: false,
      section: 'virtual_warehouse_transfer.form.sections.product_details',
      disabled: true,
    },
    {
      label: 'virtual_warehouse_transfer.form.fields.destination_virtual_warehouse_available_stock',
      controlName: 'destinationAvailableStock',
      type: 'number',
      placeholder: 'virtual_warehouse_transfer.form.placeholders.available_stock_in_destination',
      required: false,
      section: 'virtual_warehouse_transfer.form.sections.product_details',
      disabled: true,
    },
    {
      label: 'virtual_warehouse_transfer.form.fields.out_of_stock_quantity',
      controlName: 'outOfStockQuantity',
      type: 'number',
      placeholder: 'virtual_warehouse_transfer.form.placeholders.quantity_needed_to_fulfill',
      required: false,
      section: 'virtual_warehouse_transfer.form.sections.product_details',
      disabled: true,
    },
    {
      label: 'virtual_warehouse_transfer.form.fields.priority_for_out_of_stock_orders',
      controlName: 'priorityReplenish',
      type: 'radio',
      options: [
        { label: 'virtual_warehouse_transfer.form.values.yes', value: 'true' },
        { label: 'virtual_warehouse_transfer.form.values.no', value: 'false' },
      ],
      required: false,
      section: 'virtual_warehouse_transfer.form.sections.product_details',
      colspan: 2,
    },
  ];

  headerActions = [
    {
      action: 'cancel',
      label: 'virtual_warehouse_transfer.form.actions.cancel',
      severity: 'secondary' as const,
      outlined: true,
    },
    {
      action: 'save',
      label: 'virtual_warehouse_transfer.form.actions.save',
      severity: 'primary' as const,
    },
  ];

  onFormAction(event: { action: string; data?: any }): void {
    switch (event.action) {
      case 'save':
        this.saveTransfer(event.data);
        break;
      case 'cancel':
        this.cancel();
        break;
    }
  }

  // Method to populate sample stock data (in real app, this would come from API)
  populateStockData(): void {
    // This would be called when product is selected
    // For now, just populate with sample data
    const sampleData = {
      sourceStock: 150,
      availableQuantity: 100,
      destinationStock: 25,
      destinationAvailableStock: 20,
      outOfStockQuantity: 15,
    };

    // In real implementation, this would update the form controls
    console.log('Sample stock data:', sampleData);
  }

  private saveTransfer(formData?: any): void {
    // TODO: Implement save logic with actual API call
    console.log('Saving transfer...', formData);

    // For now, just show success and navigate back
    // In real implementation, this would call the API service
    alert(
      `Transfer saved successfully!\n\nTransfer Order: ${formData?.transferOrderNumber}\nSource: ${formData?.sourceWarehouse}\nDestination: ${formData?.destinationWarehouse}\nQuantity: ${formData?.transferQuantity}`
    );
    this.skipCanDeactivate = true;
    this.addFormComponent?.form?.markAsPristine();
    this.router.navigate(['/stock/transfer/virtual']);
  }

  private cancel(): void {
    // TODO: Implement cancel logic - navigate back
    console.log('Cancelling...');
    window.history.back();
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false);
  }
}
